import { useEffect, useState } from "react";
import "./index.scss";
import { PublicRoutes } from "./hooks";
import { useAppDispatch, useAppSelector } from "./services/hooks.service";
import { Footer } from "./components/common/Footer/Footer";
import { saveSettings } from "./redux/_actions/settings.action";
import { saveCCTPAssets, saveChains } from "./redux/_actions/wallet.action";
import { setChains } from "./services/localStorage.service";
import { MaintenanceMode, SettingsInit } from "./interfaces";
import { Header, ModalApp } from "./components/common";
import { useTranslation } from "react-i18next";
import { verifySocialEmail } from "./hooks/useCheckSearchParams";
import { getPackageJsonVersion } from "./redux/_actions/system.action";
import { ReactComponent as WARN } from "./assets/images/warning.svg";
import { saveChangeGridLayout } from "./redux/_actions/persist.action";
import { GRID_LAYOUT } from "./constants/grid.layout";
import { parentAppService } from "./App";
import { KycFinishStatus } from "./services/parrentApp.service";
import { KYCModal } from "./components/common/ModalApp/Modals/KYCRedirect";
import { saveUserTelegram } from "./redux/_actions/user.action";
import { errorHandler } from "./_utils/errorHandlers";
import { TradingKeyModal } from "./components/common/ModalApp/Modals/TradingKeyModal";
import { initialSystemMaintenanceState, initialSystemVersionState, useLazyGetSettingsQuery, useLazyGetCodexAssetsQuery, useGetVersionQuery, useGetMaintenanceQuery } from "@ultrade/react-sdk";

const Application = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sdkClient = useAppSelector((state) => state.sdkClient.instance);
  const rtkSdkClient = useAppSelector((state) => state.rtkSdkClient.instance);
  // const maintenance_mode.m_mode = useAppSelector((state) => state.system.maintenance_mode);
  // const new_version = useAppSelector((state) => state.system.new_version);
  const appTitle = useAppSelector((state) => state.settings.appTitle);
  const isMFT = useAppSelector((state) => state.exchange.mftCached);
  const [kycModal, setKycModal] = useState<KycFinishStatus | null>(null);

  const [getSettings] = useLazyGetSettingsQuery();
  const [getCodexAssets] = useLazyGetCodexAssetsQuery();
  const { data: {new_version} = initialSystemVersionState, refetch: refetchVersion } = useGetVersionQuery({ packageVersion: getPackageJsonVersion() });
  const { data: {maintenance_mode} = initialSystemMaintenanceState } = useGetMaintenanceQuery();

  const setSettings = async () => {
    try {
      const {unprocessedSettings, ...settings} = await getSettings(undefined, true).unwrap();
      if (settings.companyId && !sdkClient.companyId) {
        sdkClient.companyId = settings.companyId;
        rtkSdkClient.companyId = settings.companyId;
      }
      dispatch(saveSettings(unprocessedSettings));
    } catch (err) {
      console.log('ERR', err)
    }
  }

  const loadChains = async () => {
    try {
      const chains = await sdkClient.getChains();
      setChains(chains);
      dispatch(saveChains(chains));
    } catch (err) {
      console.log('ERR', err)
    }
  }

  const loadCCTPInfo = async () => {
    try {
      const [ cctpAssets, cctpUnifiedAssets ] = await Promise.all([
         sdkClient.getCCTPAssets(),
         sdkClient.getCCTPUnifiedAssets()
      ]);
      dispatch(saveCCTPAssets(cctpAssets, cctpUnifiedAssets));
    } catch (err) {
      console.log('ERR', err)
    }
  }

  const getAccountAssetsList = async () => {
    try {
      await getCodexAssets(undefined, true);
      // if (assets && assets.length) {
      //   const preparedAssets = assets.map(asset => mapAsset(asset, undefined));
      //   dispatch(saveExchangeAssets(preparedAssets));
      // }
      // dispatch(saveExchangeAssets(preparedAssets));
    } catch (err) {
      console.log('ERR', err)
    }
  };

  // const getVersion = async () => {
  //   try {
  //     const data = await sdkClient.getVersion();
  //     if (data?.version) dispatch(updateVersion(data.version));
  //   } catch (err) {
  //     console.log('ERR', err)
  //   }
  // }

  // const getMaintenance = async () => {
  //   try {
  //     const data = await sdkClient.getMaintenance();
  //     if (data) dispatch(saveMaintenanceMode(data));
  //   } catch (err) {
  //     console.log('ERR', err)
  //   }
  // }

  useEffect(() => {
    if (new_version) {
      dispatch(saveChangeGridLayout(GRID_LAYOUT, isMFT));
    }
  }, [new_version]);


  const checkQueryParams = async () => {
    if (!sdkClient?.companyId) return;
    try {
      const kycStatus = await parentAppService.getKycStatus();
      if (kycStatus) setKycModal(kycStatus);

      const emailData = await parentAppService.getEmailData();
      if (emailData) verifySocialEmail(sdkClient, emailData.email, emailData.hash);

      const telegramData = await parentAppService.getTelegramData();
      if (telegramData) {
        sdkClient.addTelegram(telegramData)
        .then(() => dispatch(saveUserTelegram(telegramData)));
        rtkSdkClient.addTelegram(telegramData)
      }

      const refferalToken = await parentAppService.getRefferalClick();
      if (refferalToken) {
        sdkClient.countAffiliateClick(refferalToken).catch(e => console.log(e));
        rtkSdkClient.countAffiliateClick(refferalToken).catch(e => console.log(e));
      }

      if (kycStatus || emailData || telegramData || refferalToken) {
        parentAppService.cleanQuery();
      }
    } catch (error) {
      console.error("Error fetching query:", error);
      errorHandler(error)
    }
  };

  useEffect(() => {
    checkQueryParams()
  }, [sdkClient?.companyId])

  useEffect(() => {
    getAccountAssetsList();
    loadChains();
    loadCCTPInfo();
    // getVersion();
    // getMaintenance();

    const intervalId = setInterval(setSettings, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      <Header />
      <div style={{ height: "calc(100vh - 95px)" }}>
        <PublicRoutes />
      </div>
      <Footer className="d-none d-sm-none d-md-none d-lg-flex" />

      {/* Expired TK */}
      <TradingKeyModal />

      {/* KYC Modal */}
      <KYCModal kycModal={kycModal} setKycModal={setKycModal} />
      
      {/* MaintenanceMode Modal */}
      {(maintenance_mode === MaintenanceMode.Full) && 
        <ModalApp 
          show={true} 
          width={400}
        >
          <p className="modal_header">{ t("obd_modals.platform_maintenance") }</p>
          <p className="modal_info">{ t("obd_modals.mtnc_info_1", {name: appTitle}) }</p>
          <p>{ t("obd_modals.mtnc_info_2") }</p>
        </ModalApp>
      }

      {/* New Version */}
      <ModalApp 
        show={new_version && maintenance_mode !== MaintenanceMode.Full} 
        width={400}
        onHide={refetchVersion}
        titleIcon={ <WARN /> }
        title={ t("obd_modals.new_version_available") }
      >
        <div className="modal_cont">
          <p>{ t("obd_modals.please_reload") }</p>
          <p className="pink-modal-text">{ t("obd_modals.application_may_not_work") }</p>
        </div>
      </ModalApp>
    </>
  );
};



export default Application;
